import { Fr3Format } from './fr3Format';
import {
    d2i, d2l, doubleCompare, isDigit, isHexDigit, isLetter, isLetterOrDigit, isWhitespace,
    javaDoubleToString, javaRound, javaTrim, parseJavaDouble, stringCompare,
} from './javaCompat';

/**
 * Evaluator for the expression dialect FastReport templates actually use
 * (port of Fr3Expr): field references, IIF/IF, SUM and the other
 * aggregates, string helpers, a little arithmetic and report variables.
 * Anything outside that vocabulary throws Fr3EvalException so the caller can
 * report it rather than print something wrong.
 *
 * Values: Java Double -> number, String -> string, Boolean -> boolean,
 * null -> null.
 *
 * A context is { field(name), sumField(name), variable(name) }; a row
 * context adds { scopeFrom(), scopeTo(), currentRow(), moveTo(row) }.
 */
export class Fr3EvalException extends Error {
    constructor(message) {
        super(message);
        this.name = 'Fr3EvalException';
    }
}

const isRowContext = (ctx) => ctx && typeof ctx.scopeFrom === 'function';

/** String.valueOf(Object) as Java prints it. */
const javaValueOf = (v) => {
    if (v === null || v === undefined) return 'null';
    if (typeof v === 'boolean') return v ? 'true' : 'false';
    if (typeof v === 'number') return javaDoubleToString(v);
    return String(v);
};

const T = { NUM: 'NUM', STR: 'STR', FIELD: 'FIELD', IDENT: 'IDENT', OP: 'OP', END: 'END' };

const tok = (type, text, num = 0) => ({ type, text, num });
const tokString = (t) => t.type + '(' + t.text + ')';

export class Fr3Expr {
    constructor(format) {
        this.format = format == null ? new Fr3Format() : format;
    }

    /** Resolves a memo's Text: literal text with bracketed expressions spliced in. */
    evalMemoText(text, ctx) {
        if (text == null || text === '') return '';
        let out = '';
        let i = 0;
        while (i < text.length) {
            const c = text[i];
            if (c !== '[') {
                out += c;
                i++;
                continue;
            }
            const end = matchBracket(text, i);
            if (end < 0) {
                out += text.substring(i);
                break;
            }
            const expr = text.substring(i + 1, end);
            const v = this.eval(expr, ctx);
            // A dataset date prints as a locale short date in FastReport, also
            // inside a longer memo ("Datum: [DM1.PrintQ."datum"]").
            out += typeof v === 'string' ? this.format.autoFormatDate(v) : this.stringify(v);
            i = end + 1;
        }
        return out;
    }

    eval(expr, ctx) {
        const toks = tokenize(expr);
        const p = new Parser(this, toks, ctx);
        const v = p.parseExpr();
        p.expectEnd();
        return v;
    }

    stringify(v) {
        if (v === null || v === undefined) return '';
        if (typeof v === 'boolean') return v ? 'True' : 'False';
        if (typeof v === 'number') {
            if (Number.isFinite(v) && Number.isInteger(v) && Math.abs(v) < 1e15) {
                const l = d2l(v);
                return String(l);
            }
            return this.format.formatFloat('0.00', v);
        }
        return String(v);
    }
}

const matchBracket = (s, open) => {
    let depth = 0;
    let inStr = false;
    for (let i = open; i < s.length; i++) {
        const c = s[i];
        if (inStr) {
            if (c === '\'') inStr = false;
            continue;
        }
        if (c === '\'') {
            inStr = true;
            continue;
        }
        if (c === '[') depth++;
        else if (c === ']') {
            depth--;
            if (depth === 0) return i;
        }
    }
    return -1;
};

const tokenize = (s) => {
    const out = [];
    let i = 0;
    const n = s.length;
    while (i < n) {
        const c = s[i];
        if (isWhitespace(c)) {
            i++;
            continue;
        }

        // <DM1.PrintQ."fld"> - the script/expression spelling of a field ref
        if (c === '<') {
            const close = fieldRefEnd(s, i);
            if (close > 0) {
                out.push(tok(T.FIELD, fieldName(s.substring(i + 1, close))));
                i = close + 1;
                continue;
            }
        }
        if (c === '\'') {
            let sb = '';
            i++;
            while (i < n) {
                if (s[i] === '\'') {
                    if (i + 1 < n && s[i + 1] === '\'') {
                        sb += '\'';
                        i += 2;
                        continue;
                    }
                    i++;
                    break;
                }
                sb += s[i];
                i++;
            }
            out.push(tok(T.STR, sb));
            continue;
        }
        // Delphi hex literal, e.g. a colour $00FF00.
        if (c === '$' && i + 1 < n && isHexDigit(s[i + 1])) {
            const start = ++i;
            while (i < n && isHexDigit(s[i])) i++;
            const raw = s.substring(start, i);
            // Java Long.parseLong(raw, 16): overflow above 0x7FFFFFFFFFFFFFFF
            // throws. Checked on the digits (no BigInt: the CRA eslint setup
            // does not know that global).
            const digits = raw.replace(/^0+/, '');
            if (digits.length > 16 || (digits.length === 16 && parseInt(digits[0], 16) > 7)) {
                throw new Error('For input string: "' + raw + '" under radix 16');
            }
            out.push(tok(T.NUM, '$' + raw, digits === '' ? 0 : parseInt(digits, 16)));
            continue;
        }
        if (isDigit(c) || (c === '.' && i + 1 < n && isDigit(s[i + 1]))) {
            const start = i;
            while (i < n && (isDigit(s[i]) || s[i] === '.')) i++;
            const raw = s.substring(start, i);
            const v = /^[0-9.]+$/.test(raw) ? parseJavaDouble(raw) : null;
            if (v === null) throw new Fr3EvalException('bad number: ' + raw);
            out.push(tok(T.NUM, raw, v));
            continue;
        }
        if (isLetter(c) || c === '_') {
            const start = i;
            while (i < n && (isLetterOrDigit(s[i]) || s[i] === '_' || s[i] === '.' || s[i] === '"')) {
                if (s[i] === '"') {
                    const close = s.indexOf('"', i + 1);
                    if (close < 0) break;
                    i = close + 1;
                } else {
                    i++;
                }
            }
            // FastReport system variables end in '#': Page#, TotalPages#.
            if (i < n && s[i] === '#') i++;
            const word = s.substring(start, i);
            // A dotted-and-quoted name is a dataset field, e.g. DM1.PrintQ."naam"
            if (word.indexOf('"') >= 0) out.push(tok(T.FIELD, fieldName(word)));
            else out.push(tok(T.IDENT, word));
            continue;
        }
        // operators
        if (i + 1 < n) {
            const two = s.substring(i, i + 2);
            if (two === '<>' || two === '<=' || two === '>=' || two === ':=') {
                out.push(tok(T.OP, two));
                i += 2;
                continue;
            }
        }
        if ('+-*/()=<>,'.indexOf(c) >= 0) {
            out.push(tok(T.OP, c));
            i++;
            continue;
        }
        throw new Fr3EvalException('unexpected character \'' + c + '\' in: ' + s);
    }
    out.push(tok(T.END, ''));
    return out;
};

/**
 * Index of the '>' closing a field reference that starts at i, or -1 when
 * this '<' is really a less-than. Only an unbroken identifier (letters,
 * digits, dots, quotes) counts, which also admits the bare <VarBetm1> form.
 */
export const fieldRefEnd = (s, i) => {
    const close = s.indexOf('>', i + 1);
    if (close < 0) return -1;
    const inner = s.substring(i + 1, close);
    if (inner === '') return -1;
    const first = inner[0];
    if (!isLetter(first) && first !== '_') return -1;
    for (let k = 0; k < inner.length; k++) {
        const c = inner[k];
        if (!isLetterOrDigit(c) && c !== '_' && c !== '.' && c !== '"') return -1;
    }
    return close;
};

/** Reduces DM1.PrintQ."naam" (or DM1.PrintQ.naam) to naam. */
export const fieldName = (raw) => {
    const s = raw == null ? '' : javaTrim(raw);
    const last = s.lastIndexOf('"');
    if (last > 0) {
        const first = s.lastIndexOf('"', last - 1);
        if (first >= 0) return s.substring(first + 1, last);
    }
    const dot = s.lastIndexOf('.');
    return dot >= 0 ? s.substring(dot + 1) : s;
};

class Parser {
    constructor(expr, toks, ctx) {
        this.e = expr;
        this.toks = toks;
        this.ctx = ctx;
        this.pos = 0;
    }

    peek() { return this.toks[this.pos]; }

    isOp(op) {
        const t = this.peek();
        return t.type === T.OP && t.text === op;
    }

    isKeyword(kw) {
        const t = this.peek();
        return t.type === T.IDENT && t.text.toLowerCase() === kw.toLowerCase();
    }

    expectEnd() {
        if (this.peek().type !== T.END) throw new Fr3EvalException('trailing tokens at ' + tokString(this.peek()));
    }

    parseExpr() { return this.parseOr(); }

    parseOr() {
        let left = this.parseAnd();
        while (this.isKeyword('or')) {
            this.pos++;
            const right = this.parseAnd();
            left = truth(left) || truth(right);
        }
        return left;
    }

    parseAnd() {
        let left = this.parseCmp();
        while (this.isKeyword('and')) {
            this.pos++;
            const right = this.parseCmp();
            left = truth(left) && truth(right);
        }
        return left;
    }

    parseCmp() {
        let left = this.parseAdd();
        while (this.peek().type === T.OP && isCmpOp(this.peek().text)) {
            const op = this.peek().text;
            this.pos++;
            const right = this.parseAdd();
            left = compare(left, right, op);
        }
        return left;
    }

    parseAdd() {
        let left = this.parseMul();
        while (this.isOp('+') || this.isOp('-')) {
            const op = this.peek().text;
            this.pos++;
            const right = this.parseMul();
            if (op === '+' && (typeof left === 'string' || typeof right === 'string')) {
                left = this.e.stringify(left) + this.e.stringify(right);
            } else {
                left = op === '+' ? num(left) + num(right) : num(left) - num(right);
            }
        }
        return left;
    }

    parseMul() {
        let left = this.parseUnary();
        while (this.isOp('*') || this.isOp('/')) {
            const op = this.peek().text;
            this.pos++;
            const right = this.parseUnary();
            const r = num(right);
            if (op === '*') left = num(left) * r;
            else left = r === 0 ? 0 : num(left) / r;
        }
        return left;
    }

    parseUnary() {
        if (this.isOp('-')) {
            this.pos++;
            return -num(this.parseUnary());
        }
        if (this.isOp('+')) {
            this.pos++;
            return this.parseUnary();
        }
        if (this.isKeyword('not')) {
            this.pos++;
            return !truth(this.parseUnary());
        }
        return this.parsePrimary();
    }

    parsePrimary() {
        const t = this.peek();
        switch (t.type) {
            case T.NUM:
                this.pos++;
                return t.num;
            case T.STR:
                this.pos++;
                return t.text;
            case T.FIELD: {
                this.pos++;
                const v = this.ctx.field(t.text);
                // <VarBetm1> uses the same angle syntax as a field but names a
                // report variable.
                if (v == null) {
                    const variable = this.ctx.variable(t.text);
                    if (variable != null) return variable;
                }
                return coerce(v);
            }
            case T.IDENT: {
                this.pos++;
                if (this.isOp('(')) return this.call(t.text);
                const lower = t.text.toLowerCase();
                if (lower === 'true') return true;
                if (lower === 'false') return false;
                if (lower === 'null') return null;
                const v = this.ctx.variable(t.text);
                if (v != null) return v;
                // A bare name may still be a field: templates are inconsistent.
                const f = this.ctx.field(t.text);
                if (f != null) return coerce(f);
                return '';
            }
            case T.OP:
                if (t.text === '(') {
                    this.pos++;
                    const v = this.parseExpr();
                    this.expect(')');
                    return v;
                }
                break;
            default:
                break;
        }
        throw new Fr3EvalException('unexpected token ' + tokString(t));
    }

    expect(op) {
        if (!this.isOp(op)) throw new Fr3EvalException('expected \'' + op + '\' but found ' + tokString(this.peek()));
        this.pos++;
    }

    args() {
        const out = [];
        this.expect('(');
        if (this.isOp(')')) {
            this.pos++;
            return out;
        }
        out.push(this.parseExpr());
        while (this.isOp(',')) {
            this.pos++;
            out.push(this.parseExpr());
        }
        this.expect(')');
        return out;
    }

    /** SUM needs the field name, not its current-row value. */
    pendingFieldName() {
        const save = this.pos;
        if (this.peek().type === T.OP && this.peek().text === '(') {
            this.pos++;
            if (this.peek().type === T.FIELD) {
                const name = this.peek().text;
                this.pos++;
                if (this.isOp(')')) {
                    this.pos++;
                    return name;
                }
                // SUM(<field>, MasterData1): the band argument only names the
                // band FastReport counts on; the scope is ours.
                if (this.isOp(',')) {
                    this.pos++;
                    this.skipArgs();
                    this.expect(')');
                    return name;
                }
            }
        }
        this.pos = save;
        return null;
    }

    /**
     * SUM(expression[, band]): re-parses the first argument once per row in
     * scope. The token position after the argument is the same for every row.
     */
    aggregate(rc, fn) {
        this.expect('(');
        const argStart = this.pos;
        const saveRow = rc.currentRow();
        let sum = 0;
        let min = null;
        let max = null;
        let count = 0;
        let argEnd = argStart;
        const from = rc.scopeFrom();
        const to = rc.scopeTo();
        if (from > to) {
            // Empty scope: parse once only to find where the argument ends.
            rc.moveTo(saveRow);
            this.parseExpr();
            argEnd = this.pos;
        }
        for (let r = from; r <= to; r++) {
            rc.moveTo(r);
            this.pos = argStart;
            const v = num(this.parseExpr());
            sum += v;
            min = min == null ? v : javaMin(min, v);
            max = max == null ? v : javaMax(max, v);
            count++;
            argEnd = this.pos;
        }
        rc.moveTo(saveRow);
        this.pos = argEnd;
        if (this.isOp(',')) {
            this.pos++;
            this.skipArgs();
        }
        this.expect(')');
        switch (fn) {
            case 'AVG': return count === 0 ? 0 : sum / count;
            case 'MIN': return min == null ? 0 : min;
            case 'MAX': return max == null ? 0 : max;
            default: return sum;
        }
    }

    /** Skips the remaining (band/flag) arguments of an aggregate without evaluating them. */
    skipArgs() {
        let depth = 0;
        while (this.peek().type !== T.END) {
            if (this.isOp('(')) depth++;
            else if (this.isOp(')')) {
                if (depth === 0) return;
                depth--;
            }
            this.pos++;
        }
    }

    call(name) {
        const fn = name.toUpperCase();
        const e = this.e;
        if (fn === 'SUM') {
            const field = this.pendingFieldName();
            if (field != null) {
                const s = this.ctx.sumField(field);
                return s == null ? 0 : s;
            }
            if (isRowContext(this.ctx)) return this.aggregate(this.ctx, 'SUM');
            const a = this.args();
            return a.length === 0 ? 0 : num(a[0]);
        }
        // The other FastReport aggregates, over the same scope as SUM.
        if ((fn === 'AVG' || fn === 'MIN' || fn === 'MAX') && isRowContext(this.ctx)) {
            return this.aggregate(this.ctx, fn);
        }
        if (fn === 'COUNT' && isRowContext(this.ctx)) {
            const rc = this.ctx;
            this.expect('(');
            this.skipArgs();
            this.expect(')');
            return Math.max(0, rc.scopeTo() - rc.scopeFrom() + 1);
        }
        const a = this.args();
        const one = () => {
            if (a.length === 0) throw new Fr3EvalException('missing argument');
            return a[0];
        };
        switch (fn) {
            case 'IIF':
            case 'IF':
                if (a.length < 3) throw new Fr3EvalException('IIF needs 3 arguments');
                return truth(a[0]) ? a[1] : a[2];
            case 'UPPERCASE':
            case 'UPPER':
                return e.stringify(one()).toUpperCase();
            case 'LOWERCASE':
            case 'LOWER':
                return e.stringify(one()).toLowerCase();
            case 'TRIM':
                return javaTrim(e.stringify(one()));
            case 'LENGTH':
                return e.stringify(one()).length;
            case 'ROUND':
                return javaRound(num(one()));
            case 'INT':
                return d2l(num(one()));
            case 'ABS':
                return Math.abs(num(one()));
            case 'FORMATFLOAT':
                if (a.length < 2) throw new Fr3EvalException('FormatFloat needs 2 arguments');
                return e.format.formatFloat(e.stringify(a[0]), num(a[1]));
            case 'FLOATTOSTR':
                return e.format.floatToStr(num(one()));
            case 'CHR':
                // Templates use Chr(13) to break a memo into lines.
                return String.fromCharCode(d2i(num(one())) & 0xFFFF);
            case 'COPY': {
                // Delphi Copy(s, index, count) - index is 1-based.
                if (a.length < 3) throw new Fr3EvalException('Copy needs 3 arguments');
                const s = e.stringify(a[0]);
                let from = d2i(num(a[1]));
                const count = d2i(num(a[2]));
                if (from < 1) from = 1;
                const start = Math.min(from - 1, s.length);
                const end = count <= 0 ? start : Math.min(start + count, s.length);
                return s.substring(start, end);
            }
            case 'STR':
            case 'INTTOSTR':
                return e.stringify(one());
            case 'DATETOSTR':
                return e.format.autoFormatDate(e.stringify(one()));
            case 'FRAC': {
                const v = num(one());
                return v - d2l(v);
            }
            case 'STRTOFLOAT':
            case 'STRTOINT':
                return num(one());
            case 'POS': {
                // Delphi Pos(substr, s): 1-based index, 0 when absent.
                if (a.length < 2) throw new Fr3EvalException('Pos needs 2 arguments');
                return e.stringify(a[1]).indexOf(e.stringify(a[0])) + 1;
            }
            default:
                throw new Fr3EvalException('unsupported function ' + name);
        }
    }
}

const isCmpOp = (s) => s === '=' || s === '<>' || s === '<' || s === '>' || s === '<=' || s === '>=';

/** Math.min/max(double, double) as Java (NaN wins, -0.0 < 0.0). */
const javaMin = (a, b) => {
    if (Number.isNaN(a) || Number.isNaN(b)) return NaN;
    return doubleCompare(a, b) <= 0 ? a : b;
};
const javaMax = (a, b) => {
    if (Number.isNaN(a) || Number.isNaN(b)) return NaN;
    return doubleCompare(a, b) >= 0 ? a : b;
};

// -- value helpers -------------------------------------------------------------

/** Field values arrive as strings; treat numeric-looking ones as numbers. */
export const coerce = (raw) => {
    if (raw == null) return '';
    const d = Fr3Format.toDouble(raw);
    return d != null ? d : raw;
};

export const num = (v) => {
    if (v === null || v === undefined) return 0;
    if (typeof v === 'number') return v;
    if (typeof v === 'boolean') return v ? 1 : 0;
    const d = Fr3Format.toDouble(javaValueOf(v));
    return d == null ? 0 : d;
};

export const truth = (v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v !== 0;
    const s = javaTrim(javaValueOf(v));
    return s !== '' && s.toLowerCase() !== 'false' && s !== '0';
};

export const compare = (a, b, op) => {
    let c;
    if (typeof a === 'string' || typeof b === 'string') {
        const da = typeof a === 'number' ? a : Fr3Format.toDouble(javaValueOf(a));
        const db = typeof b === 'number' ? b : Fr3Format.toDouble(javaValueOf(b));
        if (da != null && db != null) c = doubleCompare(da, db);
        else c = stringCompare(javaValueOf(a), javaValueOf(b));
    } else {
        c = doubleCompare(num(a), num(b));
    }
    switch (op) {
        case '=': return c === 0;
        case '<>': return c !== 0;
        case '<': return c < 0;
        case '>': return c > 0;
        case '<=': return c <= 0;
        case '>=': return c >= 0;
        default: throw new Fr3EvalException('bad comparison ' + op);
    }
};

export default Fr3Expr;
